import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  exhibitionColumns,
  exhibitionColumnsVisibility,
} from "@/config/dashboard/exhibition";
import { generateTable } from "@/config/dashboard/general";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
  useGetSimpleItinerantStops,
} from "@/api/related-fields";
import {
  Exhibition,
  ItinerantExhibitionParentType,
} from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { ExhibitionLinkGridCard } from "./relatedLinkGridCards";
import ItinerantExhibitionDrawer, {
  itinerantStopFromExhibition,
} from "./drawer/ItinerantExhibitionDrawer";
import { Button } from "../ui/button";
import { Pencil } from "lucide-react";

interface LinksProps {
  id: string;
  type: LinkableType;
}

const SIMPLE_ITINERANT_PARENTS = new Set<string>([
  "audiovisual",
  "bibliography",
  "document",
  "photography",
  "writing",
  "exhibition",
]);

/**
 * ExhibitionLinks component
 * @returns {JSX.Element}
 */
const ExhibitionLinks = ({ id, type }: LinksProps) => {
  const ACTIONS_COLUMN_ID = "actions";
  const ensureActionsColumnLast = (order: string[]) => {
    const uniqueOrder = Array.from(new Set(order));
    const withoutActions = uniqueOrder.filter(
      (columnId) => columnId !== ACTIONS_COLUMN_ID,
    );
    return [...withoutActions, ACTIONS_COLUMN_ID];
  };

  const [getPaginationRelatedExhibitions, setPaginationRelatedExhibitions] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: relatedFields } = useGetRelatedFields(
    id,
    type,
    "relatedExhibition",
    getPaginationRelatedExhibitions.pageSize,
    getPaginationRelatedExhibitions.page *
      getPaginationRelatedExhibitions.pageSize,
  );

  const { data: countRelatedExhibitions } = useCountRelatedFields(id, type, [
    "relatedExhibition",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedExhibition",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedExhibition",
  );

  const { t } = useTranslation();

  const [searchContent, setSearchContent] = useState<string>("");

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedExhibitionId, setSelectedExhibitionId] = useState<string | null>(
    null,
  );
  const [itinerantOpen, setItinerantOpen] = useState(false);
  const [itinerantSeed, setItinerantSeed] = useState<Exhibition | null>(null);
  const [itinerantGroupId, setItinerantGroupId] = useState<string | null>(null);

  const supportsItinerant = SIMPLE_ITINERANT_PARENTS.has(type);
  const parentType = type as ItinerantExhibitionParentType;

  const { data: simpleStops } = useGetSimpleItinerantStops(
    parentType,
    id,
    supportsItinerant,
  );

  const stopsByExhibitionId = useMemo(() => {
    const map = new Map<
      string,
      { groupId: string; note: string; order: number; id: string }
    >();
    for (const stop of simpleStops ?? []) {
      map.set(stop.exhibitionId, {
        groupId: stop.groupId,
        note: stop.note,
        order: stop.order,
        id: stop.id,
      });
    }
    return map;
  }, [simpleStops]);

  const toggleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedExhibitions } = useGetNotRelatedResource<Exhibition>({
    resultType: "exhibition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (exhibition: Exhibition) => {
      if (
        supportsItinerant &&
        exhibition.expositionsDescriptionExpositionItinerante
      ) {
        setItinerantSeed(exhibition);
        setItinerantGroupId(null);
        setItinerantOpen(true);
        return;
      }
      createRelatedFields([id, exhibition.id]);
    },
    [createRelatedFields, id, supportsItinerant],
  );

  const handleEditItinerant = useCallback(
    (exhibition: Exhibition) => {
      const meta = stopsByExhibitionId.get(exhibition.id);
      if (!meta) return;
      const groupStops = (simpleStops ?? []).filter(
        (s) => s.groupId === meta.groupId,
      );
      setItinerantSeed(exhibition);
      setItinerantGroupId(meta.groupId);
      // seed via initialStops rebuilt from groupStops in drawer open effect —
      // store on seed by setting a custom open with group
      setItinerantOpen(true);
      // stash group stops on a ref via seed + simpleStops lookup in render
      void groupStops;
    },
    [simpleStops, stopsByExhibitionId],
  );

  const handleDeleteRelation = useCallback(
    (exhibitionId: string) => {
      deleteRelation(exhibitionId);
    },
    [deleteRelation],
  );

  const itinerantInitialStops = useMemo(() => {
    if (!itinerantGroupId || !simpleStops) return undefined;
    return simpleStops
      .filter((s) => s.groupId === itinerantGroupId)
      .sort((a, b) => a.order - b.order)
      .map((s) =>
        itinerantStopFromExhibition(s.exhibition, s.id, {
          note: s.note,
        }),
      );
  }, [itinerantGroupId, simpleStops]);

  const baseColumns = useMemo(
    () => generateTable(exhibitionColumns, "exhibition", t),
    [t],
  );

  const renderExhibitionRowActions = useCallback(
    (item: Exhibition) => {
      const itinerantMeta = stopsByExhibitionId.get(item.id);
      return (
        <div className="flex items-center justify-end gap-1">
          {!isAddMode && itinerantMeta && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => handleEditItinerant(item)}
              title={t("expositionsDescriptionExpositionItinerante")}
            >
              <Pencil className="h-4 w-4 text-gray-500" />
            </Button>
          )}
          <RelatedRecordRowActions
            isAddMode={isAddMode}
            resourceId={item.id}
            previewType="exhibition"
            previewElements={[
              {
                label: t("expositionsDescriptionNumeroExpo"),
                value: item.expositionsDescriptionNumeroExpo,
                type: "text",
              },
              {
                label: t("attachments"),
                value: item.attachments,
                type: "image",
              },
              {
                label: t("expositionsDescriptionResume"),
                value: item.expositionsDescriptionResume,
                type: "richtext",
              },
            ]}
            onDelete={() => handleDeleteRelation(item.id)}
            onAdd={() => handleCreateRelation(item)}
          />
        </div>
      );
    },
    [
      handleCreateRelation,
      handleDeleteRelation,
      handleEditItinerant,
      isAddMode,
      stopsByExhibitionId,
      t,
    ],
  );

  const columns: ColumnDef<Exhibition, any>[] = useMemo(() => {
    return [
      ...baseColumns,
      createActionsColumnDef<Exhibition>(({ row }) =>
        renderExhibitionRowActions(row.original),
      ),
    ];
  }, [baseColumns, renderExhibitionRowActions]);

  const STORAGE_KEY = "exhibition-links-table";
  const defaultVisibility = { ...exhibitionColumnsVisibility, actions: true };
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    },
  );
  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { order, columnOrder } = saved;
        const loadedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : [];
        return ensureActionsColumnLast(loadedOrder as string[]);
      }
    } catch {
      // ignore
    }
    return ensureActionsColumnLast([]);
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: { columnVisibility, columnOrder: columnsOrder },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedExhibitions,
    setPage: setPaginationRelatedExhibitions,
    totalSize: countRelatedExhibitions?.relatedExhibition ?? 0,
  };

  return (
    <>
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY}
        title={t("linkedExhibitions")}
        isAddMode={isAddMode}
        onBack={toggleAddMode}
        onToggleAdd={toggleAddMode}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        addModeTable={
          <DataTable
            columns={columns}
            data={notRelatedExhibitions?.data ?? []}
            pagination={{
              pagination: getPagination,
              setPage: setPagination,
              totalSize: notRelatedExhibitions?.totalSize ?? 0,
            }}
            storageKey={STORAGE_KEY}
            options={tableOptions}
          />
        }
        listTable={
          <DataTable
            columns={columns}
            data={relatedFields ?? []}
            pagination={relatedPaginationProps}
            storageKey={STORAGE_KEY}
            options={tableOptions}
          />
        }
        gridContent={
          <>
            {relatedFields?.map((row: Exhibition) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderExhibitionRowActions(row)}>
                  <ExhibitionLinkGridCard
                    item={row}
                    t={t}
                    onTextClick={() => setSelectedExhibitionId(row.id)}
                  />
                </GridCardWithActions>
                <ShowElements
                  type="exhibition"
                  id={row.id}
                  open={selectedExhibitionId === row.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedExhibitionId(null);
                    }
                  }}
                  trigger={<span />}
                  elements={[
                    {
                      label: t("expositionsDescriptionNumeroExpo"),
                      value: row.expositionsDescriptionNumeroExpo,
                      type: "text",
                    },
                    {
                      label: t("expositionsDescriptionResume"),
                      value: row.expositionsDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.attachments,
                      type: "image",
                    },
                  ]}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />

      {supportsItinerant && itinerantSeed && (
        <ItinerantExhibitionDrawer
          open={itinerantOpen}
          onOpenChange={(open) => {
            setItinerantOpen(open);
            if (!open) {
              setItinerantSeed(null);
              setItinerantGroupId(null);
            }
          }}
          parentType={parentType}
          parentId={id}
          groupId={itinerantGroupId}
          seedExhibition={itinerantSeed}
          initialStops={itinerantInitialStops}
          showCatalogNumber={false}
          showExemplaireExpose={false}
        />
      )}
    </>
  );
};

export default ExhibitionLinks;
